import { PacketType } from "../enum/PacketType";
import { PacketValueSerialType } from "../enum/PacketValueSerialType";
import { ScriptTypeMap } from "../enum/ScriptTypeMap";
import Vector2 from "../math/Vector2";
import Bullet from "../scripts/Bullet";
import Explosion from "../scripts/Explosion";
import Player from "../scripts/Player";
import Packet from "../server/Packet";
import { PacketValue } from "../type/PacketValue";
import { AddValuesToPacket, clamp, ReadValuesFromPacket } from "../util";
import Color from "../util/Color";
import Listener from "../util/Listener";
import { Random } from "../util/Random";
import Engine, { EngineOptions } from "./Engine";
import GameObject from "./GameObject";
import { Input } from "./Input";
import MonoBehavior from "./MonoBehavior";
import NetworkBehavior from "./NetworkBehavior";
import { Time } from "./Time";

export default class NetEngine extends Engine {

    packetListener: Listener<Packet> = new Listener<Packet>();
    socket: WebSocket;
    pingTime: number = 0;
    latency: number[] = [];

    private _isHost = false;
    clientId: string = "";
    clientName: string;
    clientPlayer: Player|undefined;

    logs: {message: string; timestamp: number}[] = [];

    get isHost() {return this._isHost;}

    constructor(clientName: string, serverAddress: string, canvas: HTMLCanvasElement, options?: EngineOptions) {
        super(canvas, options);
        this.clientName = clientName;
        this.socket = new WebSocket(serverAddress);
    }

    addLog(msg: string) {
        this.logs.push({
            message: msg,
            timestamp: Time.time
        });
    }

    sendChat(msg: string) {
        msg = `${msg}`;
        this.addLog(msg);

        this.sendPacket(PacketType.ChatSend, packet => {
            packet.writeString(msg);
        });
    }

    sendPacket(type: PacketType, cb?: (packet: Packet) => void) {
        if (!this.clientId && type !== PacketType.RequestJoin) return; //don't try to send game packets if you don't have an id yet
        const packet = new Packet(type);
        packet.writeString(this.clientId);
        if (cb != undefined) cb(packet);
        
        if (this.socket.readyState === 1) this.socket.send(packet.toBuffer());
    }

    createExplosionForAll(options: {
        position: Vector2;
        size: number;
        color: Color;
    }) {
        const [, expl] = this.NewGameObjectWithScript<Explosion>(Explosion);
        expl.transform.position = options.position;
        expl.size = options.size;
        expl.w = expl.size * 2;
        expl.color = options.color;

        this.sendCreatePacket(expl.gameObject, {
            type: "EXPLOSION",
            values: [
                {
                    key: "size",
                    value: expl.size,
                    type: PacketValueSerialType.UInt
                },
                {
                    key: "w",
                    value: expl.w,
                    type: PacketValueSerialType.UInt
                },
                {
                    key: "color",
                    value: expl.color,
                    type: PacketValueSerialType.Color
                },
            ]
        });
    }

    createBulletForAll(position: Vector2, velocity: Vector2, values: PacketValue[] = []) {
        const [, bullet] = this.NewGameObjectWithScript<Bullet>(Bullet);
        bullet.transform.position = position;
        bullet.velocity = velocity;
        values.forEach(v => {
            //@ts-ignore
            bullet[v.key] = v.value;
        });
        bullet.ownerId = this.clientId;

        this.sendCreatePacket(bullet.gameObject, {
            type: "BULLET",
            values: [
                {
                    key: "velocity",
                    value: bullet.velocity,
                    type: PacketValueSerialType.Vector2
                },
                {
                    key: "ownerId",
                    value: bullet.ownerId,
                    type: PacketValueSerialType.String
                },
                ...values
            ]
        });
    }

    createExplosion(options: {
        position: Vector2;
        size: number;
        color: Color;
    }) {
        const [, expl] = this.NewGameObjectWithScript<Explosion>(Explosion);
        expl.transform.position = options.position;
        expl.size = options.size;
        expl.w = expl.size * 2;
        expl.color = options.color;
    }

    sendUpdateTransformPacket(obj: GameObject) {
        this.sendPacket(PacketType.UpdateTransform, packet => {
            packet.writeString(obj.id);
            packet.writeTransform(obj.transform);
        });
    }

    /**
     * update transform and desired scripts
     * @param obj the gameobject to sync
     */
    sendUpdateObjectPacket(obj: GameObject, scriptValues: Record<string, PacketValue[]>) {
        this.sendPacket(PacketType.UpdateObject, packet => {
            packet.writeString(obj.id);
            packet.writeTransform(obj.transform);
            packet.writeUInt32(Object.keys(scriptValues).length);

            for (const scriptKey in scriptValues) {
                const values = scriptValues[scriptKey];
                packet.writeString(scriptKey);
                AddValuesToPacket(packet, values);
            }
        });
    }

    sendUpdatePacket(script: MonoBehavior, scriptType: string, values: PacketValue[]) {
        this.sendPacket(PacketType.UpdateScript, packet => {
            packet.writeString(script.gameObject.id);
            packet.writeString(scriptType);
            AddValuesToPacket(packet, values);
        });
    }

    sendAddScriptPacket(obj: GameObject, scriptOptions: {
        type: string;
        values: PacketValue[];
    }) {
        this.sendPacket(PacketType.CreateScript, packet => {
            packet.writeString(obj.id);
            packet.writeString(scriptOptions.type);
            AddValuesToPacket(packet, scriptOptions.values);
        });
    }

    sendCreatePacket(obj: GameObject, scriptOptions?: {
        type: string;
        values: PacketValue[];
    }) {
        const bInitScript = scriptOptions != undefined;

        this.sendPacket(PacketType.CreateObject, packet => {
            packet.writeString(obj.id);
            packet.writeTransform(obj.transform);
            packet.writeBool(bInitScript);

            if (bInitScript) {
                packet.writeString(scriptOptions.type);
                AddValuesToPacket(packet, scriptOptions.values);
            }
        });
    }

    sendRemovePacket(obj: GameObject) {
        this.sendPacket(PacketType.RemoveObject, packet => {
            packet.writeString(obj.id);
        });
    }

    sendDamagePacket(target: Player, amt: number) {
        this.sendPacket(PacketType.DamagePlayer, packet => {
            packet.writeString(target.gameObject.id);
            packet.writeFloat32(amt);
        });
    }

    sendHealPacket(target: Player, amt: number) {
        this.sendPacket(PacketType.HealPlayer, packet => {
            packet.writeString(target.gameObject.id);
            packet.writeFloat32(amt);
        });
    }

    sendRestoreEnergyPacket(target: Player, amt: number) {
        this.sendPacket(PacketType.RestoreEnergy, packet => {
            packet.writeString(target.gameObject.id);
            packet.writeFloat32(amt);
        });
    }

    protected onPlayerJoin(clientId: string, playerName: string) {}
    protected onPlayerLeave(clientId: string) {}
    protected onWelcome(clientId: string, playerList: {id: string; name: string;}[]) {}
    protected onInit(): void {

        //#region packet listeners
        this.packetListener.on(`pkt-${PacketType.HealPlayer}`, packet => {
            const senderId = packet.readString();
            const targetId = packet.readString();
            const amt = packet.readFloat32();

            const target: Player = this.GetGameObject(targetId)?.GetScript(Player);

            if (!target) return;

            if (this.clientPlayer?.Equals(target)) {
                this.clientPlayer.Heal(amt);
            }
        });

        this.packetListener.on(`pkt-${PacketType.RestoreEnergy}`, packet => {
            const senderId = packet.readString();
            const targetId = packet.readString();
            const amt = packet.readFloat32();

            const target: Player = this.GetGameObject(targetId)?.GetScript(Player);

            if (!target) return;

            if (this.clientPlayer?.Equals(target)) {
                this.clientPlayer.energy += amt;
                this.clientPlayer.energy = clamp(this.clientPlayer.energy, 0, this.clientPlayer.maxEnergy)
            }
        });

        this.packetListener.on(`pkt-${PacketType.DamagePlayer}`, packet => {
            const senderId = packet.readString();
            const targetId = packet.readString();
            const amt = packet.readFloat32();

            const target: Player = this.GetGameObject(targetId)?.GetScript(Player);

            if (!target) return;

            if (this.clientPlayer?.Equals(target)) {
                this.clientPlayer.Damage(amt, senderId);
            }
        });

        this.packetListener.on(`pkt-${PacketType.SetPlayerClass}`, packet => {
            const senderId = packet.readString();
            const player: Player = this.GetGameObject(senderId)?.GetScript(Player);

            if (!player) return;

            player.SetClass(packet.readBytes(1)[0]);
        });

        this.packetListener.on(`pkt-${PacketType.PlayerJoin}`, packet => {
            const clientId = packet.readString();
            const clientName = packet.readString();

            this.onPlayerJoin(clientId, clientName);
        });

        this.packetListener.on(`pkt-${PacketType.PlayerLeave}`, packet => {
            const clientId = packet.readString();
            
            this.onPlayerLeave(clientId);

        });

        this.packetListener.on(`pkt-${PacketType.ClientWelcome}`, (packet) => {
            this.clientId = packet.readString();
            const playerCount = packet.readUInt32();
            const players: {id: string; name: string;}[] = [];

            for (let i = 0; i < playerCount; i++) {
                const id = packet.readString();
                const name = packet.readString();

                players.push({id, name});
            }

            this.onWelcome(this.clientId, players);

            setInterval(() => {
                this.pingTime = Time.time;
                this.sendPacket(PacketType.Ping);
            }, 333.33);
        });

        this.packetListener.on(`pkt-${PacketType.ChatSend}`, packet => {
            const senderId = packet.readString();
            const msg = packet.readString();
            this.addLog(msg);
        });

        this.packetListener.on(`pkt-${PacketType.UpdateObject}`, packet => {
            const senderId = packet.readString();
            const objId = packet.readString();
            const transformData = packet.readTransform();
            const obj = this.GetGameObject(objId);

            if (!obj) return;

            obj.transform.position = transformData.position;
            obj.transform.rotation = transformData.rotation;
            obj.transform.scale = transformData.scale;

            const numScriptUpdates = packet.readUInt32();

            for (let i = 0; i < numScriptUpdates; i++) {
                const sType = packet.readString();
                const values = ReadValuesFromPacket(packet);
                const T = ScriptTypeMap[sType];
                const script = obj.GetScript(T);

                if (!script) continue;

                Object.assign(script, values);
            }
        });

        this.packetListener.on(`pkt-${PacketType.CreateScript}`, packet => {
            const senderId = packet.readString();
            const objId = packet.readString();
            const sScriptType = packet.readString();
            const scriptValues = ReadValuesFromPacket(packet);
            const T = ScriptTypeMap[sScriptType];

            if (!T) return;

            const obj = this.GetGameObject(objId);

            if (!obj) return;

            const script: MonoBehavior = obj.AddScript(T);

            Object.assign(script, scriptValues);
        });

        this.packetListener.on(`pkt-${PacketType.RemoveObject}`, packet => {
            const senderId = packet.readString();
            const objId = packet.readString();

            this.RemoveGameObject(objId);
        });

        this.packetListener.on(`pkt-${PacketType.UpdateTransform}`, packet => {
            const senderId = packet.readString();
            const objectId = packet.readString();
            const transformData = packet.readTransform();

            

            const obj = this.GetGameObject(objectId);

            if (!obj) return;

            obj.transform.position = transformData.position;
            obj.transform.rotation = transformData.rotation;
            obj.transform.scale = transformData.scale;
        });

        this.packetListener.on(`pkt-${PacketType.UpdateScript}`, packet => {
            const senderId = packet.readString();
            const objectId = packet.readString();
            let obj = this.GetGameObject(objectId);

            if (!obj) return;

            const scriptTypeString = packet.readString();
            const T = ScriptTypeMap[scriptTypeString];

            if (T == undefined) return;

            const script = obj.GetScript(T);

            if (!script) return;

            const values = ReadValuesFromPacket(packet);

            Object.assign(script, values);
        });

        this.packetListener.on(`pkt-${PacketType.CreateObject}`, packet => {
            const senderId = packet.readString();
            const objectId = packet.readString();
            const transformData = packet.readTransform();
            const bInitScript = packet.readBool();

            const obj = this.NewGameObject(objectId);
            obj.transform.position = transformData.position;
            obj.transform.rotation = transformData.rotation;
            obj.transform.scale = transformData.scale;

            if (bInitScript) {
                const scriptTypeString = packet.readString();
                const T = ScriptTypeMap[scriptTypeString];

                if (T == undefined) return;

                const script: NetworkBehavior = obj.AddScript(T);
                script.isOwner = false;

                const initialValues = ReadValuesFromPacket(packet);

                Object.assign(script, initialValues);
            }
        });

        this.packetListener.on(`pkt-${PacketType.BecomeHost}`, () => {
            this._isHost = true;
        });

        this.packetListener.on(`pkt-${PacketType.Ping}`, () => {
            const timeMs = ((Time.time - this.pingTime) * 1000);
            this.latency.push(timeMs);
            
            if (this.latency.length > 10) this.latency.shift();
        });
        //#endregion

        this.socket.addEventListener("open", () => {

            console.log("Connected to the server");

            this.socket.addEventListener("message", async (ev) => {
                const data = ev.data;
                const buffer = await data.arrayBuffer();
                const packet = Packet.Read(new Uint8Array(buffer));
                const type = packet.readType();

                this.packetListener.send(`pkt-${type}`, packet);
            });

            this.sendPacket(PacketType.RequestJoin, packet => {
                packet.writeString(this.clientName);
            });
        });
    }

    protected syncAccumulator = 0;
    protected syncDelta = 1/30;
    private firstTick = true;
    protected Tick = (ts: number) => {
    
    
        if (this._running) {
            window.requestAnimationFrame(this.Tick);
            // setTimeout(() => {
            //     this.Tick(performance.now());
            // }, 1000/120);
        }

        const newTime = ts/1000;

        if (this.firstTick) {
            this.firstTick = false;
            this.realTime = newTime;
        }

        let frameTime = (newTime - this.realTime) * Time.scale;
        this.realFPS.push(1/frameTime);

        if (this.realFPS.length > 1024) {
            this.realFPS.shift();
        }

        // if (frameTime > 0.1) frameTime = 0.1;

        this.realTime = newTime;


        this.accumulator += frameTime;
        this.syncAccumulator += frameTime;

        Time.deltaTime = this.targetDelta;
        let steps = 0;
        while (this.accumulator >= this.targetDelta) {
            this.onBeforeUpdate();
            this.Update();
            this.LateUpdate();

            Input.ClearThisFrame();

            this.accumulator -= this.targetDelta;
            Time.time += this.targetDelta;
            
            if (++steps >= this.maxSteps) break;
        }

        this.Draw();
        this.DrawGizmos();

        if (this.syncAccumulator >= this.syncDelta) {
            // this.clientPlayer?.SyncTransform();
            if (this.clientPlayer) {
                this.clientPlayer.Sync();
            }
            
            this.syncAccumulator -= this.syncDelta;
        }
    }

    protected Draw(): void {
        super.Draw();

        this.drawTexts(
            this.logs.filter(v => Time.time - v.timestamp <= 5).map(v => v.message),
            new Vector2(8, this.height-92),
            {
                baseline: 'bottom',
                color: this.viewport.fColor
            }
        );

        const avgPing = this.latency.length === 0 ? -1 : Math.floor(this.latency.reduce((a,b) => a+b)/this.latency.length);
        
        const lightColor = avgPing === -1 ? 'grey' : avgPing < 100 ? '#00ff00' : avgPing < 200 ? '#ffff55' : 'red';
        

        this.ctx.fillStyle = lightColor;
        this.FillCircleScreen(new Vector2(this.width - 48, 13), 4);

        this.drawTexts(
            [
                `${this.latency.length === 0 ? "--" : Math.floor(this.latency.reduce((a,b) => a+b)/this.latency.length)} ms`,
                `${this.isHost ? 'Host' : "Client"}`
            ],
            new Vector2(this.width - 8, 8),
            {
                align: 'right',
                color: this.viewport.fColor
            }
        );
    }

}